import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Context } from "../../../context/Context";
import { Archive, Button, CheckCheck, CircleX, Input, Loader, NoticeBox, RefreshCcw, Select, Sparkles, Tags } from "../../ui";
import { useModalLayer } from "../../modals/modalHooks";
import { ConfirmModal } from "../../modals/Confirm";
import { menuOptionLabel } from "../topActions/shared";
import { archiveAiImportAPI, fetchAiImportAPI, retryAiImportAPI, updateAiImportAPI } from "../../../utils/api/menuAiImport";
import AiImportSourceViewer from "./AiImportSourceViewer";
import AiImportItemCard from "./AiImportItemCard";
import AiImportAdditionCard from "./AiImportAdditionCard";
import AiImportField from "./AiImportField";
import AiImportPlanDialog from "./AiImportPlanDialog";
import AiImportApproveAllDialog from "./AiImportApproveAllDialog";
import AiBalanceNotice, { aiWalletDraftFailure, aiWalletDraftNoticeOf, aiWalletNoticeOf } from "../../billing/aiWallet/AiBalanceNotice";
import AiWalletUsageSummary from "../../billing/aiWallet/AiWalletUsageSummary";
import { notifyAiWalletChanged } from "../../billing/aiWallet/aiWalletMenu";
import { aiImportText } from "./aiImportText";
import { menuErrorMessage } from "../menuRole";
import { ACTIVE_STATUSES, STATUS_VARIANT, aiImportErrorMessage, filterCounts, matchesFilter, pairText } from "./aiImportModel";
import "./AiImport.css";

const POLL_MS = 3000;
const FILTERS = ["all", "extracted", "ai", "verify", "low", "duplicates", "additions", "excluded"];

function Summary({ draft, t }) {
  const s = draft.summary;
  if (!s) return null;
  const aiParts = Object.entries(t.aiAddedParts)
    .map(([key, format]) => (s.aiAdded?.[key] ? format(s.aiAdded[key]) : null))
    .filter(Boolean);
  return (
    <section className="ai-import-summary" aria-label={t.summaryDetected(s.detected)}>
      <p className="ai-import-summary-main">{t.summaryDetected(s.detected)}</p>
      {aiParts.length > 0 && (
        <p className="ai-import-summary-ai">
          <Sparkles size={14} aria-hidden="true" /> <strong>{t.aiAdded}:</strong> {aiParts.join(" · ")}
        </p>
      )}
      <div className="ai-import-summary-flags">
        {s.needsVerification > 0 && <Tags variant="danger" size="sm">{t.needsVerification(s.needsVerification)}</Tags>}
        {s.possibleDuplicates > 0 && <Tags variant="warning" size="sm">{t.possibleDuplicates(s.possibleDuplicates)}</Tags>}
        {s.unresolvedPrices > 0 && <Tags variant="danger" size="sm">{t.unresolvedPrices(s.unresolvedPrices)}</Tags>}
      </div>
    </section>
  );
}

// Full-screen review of one AI draft: original source next to the editable
// draft. Every edit is saved immediately on the server (the draft survives
// reloads); nothing reaches the live menu before "Approve import".
export default function AiImportReview({ authorize, draftId, menus, canWrite, onClose, onApproved, onChanged }) {
  const { lang } = useContext(Context);
  const t = aiImportText(lang);
  const ref = useRef(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [walletNotice, setWalletNotice] = useState(null);
  const [filter, setFilter] = useState("all");
  const [pane, setPane] = useState("draft");
  const [saving, setSaving] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  // Approve All: the confirmation, then the result of the approval it ran.
  const [approveAllOpen, setApproveAllOpen] = useState(false);
  const [approveAllResult, setApproveAllResult] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [menuNameInput, setMenuNameInput] = useState("");
  const revisionRef = useRef(null);
  const queueRef = useRef(Promise.resolve());

  useModalLayer(true, { containerRef: ref, onEscape: (event) => !event?.defaultPrevented && !planOpen && !approveAllOpen && !confirmArchive && onClose() });

  const apply = useCallback((next) => {
    revisionRef.current = next.revision;
    setDraft(next);
    setMenuNameInput(next.intent?.menuName || "");
  }, []);

  const load = useCallback(async () => {
    try {
      apply(await fetchAiImportAPI(draftId));
    } catch (err) {
      setError(aiImportErrorMessage(err, t, lang));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, lang]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll while the backend is still working on the draft.
  const active = draft && ACTIVE_STATUSES.includes(draft.status);
  // Processing ended (charged, released or refused): the balance indicator re-reads the wallet.
  const wasActive = useRef(false);
  useEffect(() => {
    if (wasActive.current && draft && !active) notifyAiWalletChanged();
    wasActive.current = Boolean(active);
  }, [active, draft]);
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [active, load]);

  // The draft's own restaurant decides (NA-010 / NA-011), not the restaurant selected on
  // the page. Until the draft is loaded nothing is allowed; the latest `authorize`
  // is read through a ref so a queued edit is checked when it runs.
  const authorizeRef = useRef(authorize);
  authorizeRef.current = authorize;
  const draftRef = useRef(null);
  draftRef.current = draft;
  const allowedNow = () => {
    const current = draftRef.current;
    if (!current) return false;
    return Boolean(authorizeRef.current?.(current.restaurant?._id ?? current.restaurant));
  };
  const allowed = Boolean(draft) && Boolean(authorize?.(draft.restaurant?._id ?? draft.restaurant));
  const deny = () => setError(menuErrorMessage({ status: 403 }, lang));

  // Edits are sent one request at a time, each with the latest revision.
  const send = useCallback(
    (payload) => {
      queueRef.current = queueRef.current.then(async () => {
        if (!allowedNow()) return deny();
        setSaving(true);
        setError("");
        try {
          apply(await updateAiImportAPI(draftId, { revision: revisionRef.current, ...payload }));
          onChanged?.();
        } catch (err) {
          setError(aiImportErrorMessage(err, t, lang));
          if (err.code === "DRAFT_CHANGED") await load();
        } finally {
          setSaving(false);
        }
      });
      return queueRef.current;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draftId, lang, load],
  );
  const sendOps = useCallback((operations) => send({ operations }), [send]);

  const proposal = draft?.proposal;
  const editable = canWrite && allowed && draft?.status === "needs_review";
  const sectionsByKey = useMemo(() => new Map((proposal?.sections || []).map((s) => [s.key, s])), [proposal]);
  const counts = useMemo(() => filterCounts(proposal), [proposal]);
  const visibleItems = useMemo(() => (proposal ? proposal.items.filter((item) => matchesFilter(item, filter, sectionsByKey)) : []), [proposal, filter, sectionsByKey]);
  const targetMenus = menus.filter((m) => m.lifecycle !== "archived");
  const targetMenu = targetMenus.find((m) => String(m._id) === String(draft?.intent?.menuId));
  // Something left to approve: at least one entry the reviewer did not reject
  // (the dialog shows the exact counts from the backend).
  const hasRemaining = Boolean(proposal?.items?.some((item) => item.status !== "exclude" && item.duplicate?.decision !== "ignore"));

  const archive = async () => {
    if (!allowedNow()) return deny();
    try {
      apply(await archiveAiImportAPI(draftId));
      onChanged?.();
    } catch (err) {
      setError(aiImportErrorMessage(err, t, lang));
    }
  };
  const retry = async () => {
    if (!allowedNow()) return deny();
    setWalletNotice(null);
    try {
      apply(await retryAiImportAPI(draftId));
      onChanged?.();
    } catch (err) {
      const notice = aiWalletNoticeOf(err);
      setWalletNotice(notice);
      if (notice) notifyAiWalletChanged();
      else setError(aiImportErrorMessage(err, t, lang));
    }
  };
  // A run the AI Wallet refused: balance now, required amount and shortage.
  const draftWalletNotice = aiWalletDraftNoticeOf(draft);

  const renderSections = () => {
    if (filter === "additions") {
      if (!proposal.additions.length) return <p className="menu-note">{t.noMatches}</p>;
      return proposal.additions.map((addition) => <AiImportAdditionCard key={addition.key} addition={addition} items={proposal.items} relationships={proposal.relationships} canEdit={editable} onOps={sendOps} />);
    }
    if (!visibleItems.length) return <p className="menu-note">{t.noMatches}</p>;
    return proposal.sections
      .map((section) => ({ section, items: visibleItems.filter((i) => i.sectionKey === section.key) }))
      .filter(({ items }) => items.length)
      .map(({ section, items }) => (
        <section key={section.key} className="ai-import-section" aria-label={pairText(section.name, lang)}>
          <header className="ai-import-section-head">
            <h3 dir="auto">{pairText(section.name, lang)}</h3>
            {section.origin === "AI_INFERRED" && (
              <Tags variant="info" size="sm" icon={<Sparkles size={12} />}>
                {t.inferredSection}
              </Tags>
            )}
            {section.reason && <span className="menu-secondary" dir="auto">{section.reason}</span>}
          </header>
          <div className="ai-import-fields ai-import-section-fields">
            <AiImportField label={t.fields.nameAr} field={section.name.ar} dir="rtl" disabled={!editable} onOp={(op) => sendOps([{ ...op, entity: "section", key: section.key, field: "name.ar" }])} />
            <AiImportField label={t.fields.nameEn} field={section.name.en} dir="ltr" disabled={!editable} onOp={(op) => sendOps([{ ...op, entity: "section", key: section.key, field: "name.en" }])} />
            {draft.intent.mode !== "new_menu" && targetMenu && section.duplicates?.length > 0 && (
              <Select
                label={t.fields.section}
                value={section.decision === "reuse" ? String(section.targetSectionId) : "__new"}
                disabled={!editable}
                options={[{ value: "__new", label: t.sectionDecision.create }, ...section.duplicates.map((s) => ({ value: String(s.sectionId), label: `${t.sectionDecision.reuse}: ${s.name?.en || s.name?.ar || ""}` }))]}
                onChange={(e) => sendOps([e.target.value === "__new" ? { op: "setSectionDecision", key: section.key, decision: "create" } : { op: "setSectionDecision", key: section.key, decision: "reuse", targetSectionId: e.target.value }])}
              />
            )}
          </div>
          {items.map((item) => (
            <AiImportItemCard key={item.key} item={item} sections={proposal.sections} canEdit={editable} onOps={sendOps} />
          ))}
        </section>
      ));
  };

  return (
    <div className="food-modal-overlay ai-import-overlay">
      <div ref={ref} tabIndex={-1} className="ai-import-review" role="dialog" aria-modal="true" aria-label={t.reviewTitle} dir={lang === "ar" ? "rtl" : "ltr"} aria-busy={saving || undefined}>
        <header className="ai-import-review-head">
          <div>
            <h2>
              <Sparkles size={18} aria-hidden="true" /> {t.reviewTitle}
            </h2>
            {draft && (
              <p className="menu-secondary">
                {draft.source.fileName}
                {draft.ai?.model ? ` · ${t.modelInfo(draft.ai.model)}` : ""}
              </p>
            )}
          </div>
          {draft && (
            <Tags variant={STATUS_VARIANT[draft.status]} size="sm" icon={active ? <Loader size={13} className="ai-import-spin" /> : null}>
              {t.status[draft.status]}
            </Tags>
          )}
          {saving && <Loader size={16} className="ai-import-spin" aria-label="saving" />}
          <Button type="button" variant="warning" appearance="soft" size="sm" rounded icon={<CircleX size={17} />} aria-label={t.close} title={t.close} onClick={onClose} />
        </header>

        {error && (
          <p className="menu-error" role="alert">
            {error}
          </p>
        )}
        <AiBalanceNotice notice={walletNotice} lang={lang} />
        {!draft && !error && <p className="menu-note">{t.loadingSource}</p>}

        {draft && active && (
          <NoticeBox variant="info" size="sm" title={t.status[draft.status]}>
            {t.stages[draft.processing?.stage] ? `${t.stages[draft.processing.stage]} · ` : ""}
            {t.processingNote}
          </NoticeBox>
        )}
        {draft && !active && <AiWalletUsageSummary usage={draft.walletUsage} lang={lang} />}
        {draft?.status === "failed" && draftWalletNotice && !walletNotice && <AiBalanceNotice notice={draftWalletNotice} lang={lang} />}
        {draft?.status === "failed" && (
          <NoticeBox variant="danger" size="sm" title={t.failedTitle}>
            <p>{draftWalletNotice ? aiWalletDraftFailure(draftWalletNotice.code, lang) : aiWalletDraftFailure(draft.processing?.error?.code, lang) || draft.processing?.error?.message}</p>
            {canWrite && draft.processing?.error?.retryable !== false && (
              <Button type="button" variant="primary" appearance="soft" size="sm" rounded icon={<RefreshCcw size={14} />} onClick={retry}>
                {t.retry}
              </Button>
            )}
          </NoticeBox>
        )}
        {draft?.status === "archived" && <NoticeBox variant="neutral" size="sm">{t.archivedNote}</NoticeBox>}
        {draft?.status === "approved" && approveAllResult && (
          <NoticeBox variant="success" size="sm" title={t.approveAll.done(approveAllResult.result?.bulk?.entries?.approve ?? 0, approveAllResult.result?.bulk?.entries?.rejected ?? 0)}>
            <p>{t.approvedNote}</p>
            <Button type="button" variant="success" appearance="soft" size="sm" rounded onClick={() => onApproved?.(approveAllResult)}>
              {t.approveAll.openMenu}
            </Button>
          </NoticeBox>
        )}
        {draft?.status === "approved" && !approveAllResult && <NoticeBox variant="success" size="sm">{t.approvedNote}</NoticeBox>}

        {proposal && (
          <>
            <Summary draft={draft} t={t} />

            {editable && (
              <section className="ai-import-intent" aria-label={t.intent}>
                <Select
                  label={t.intent}
                  value={draft.intent.mode}
                  fullWidth={false}
                  options={["new_menu", "add_to_menu", "update_menu"].filter((m) => m === "new_menu" || targetMenus.length).map((m) => ({ value: m, label: t.modes[m] }))}
                  onChange={(e) => send({ intent: { mode: e.target.value, menuId: e.target.value === "new_menu" ? null : draft.intent.menuId || targetMenus[0]?._id } })}
                />
                {draft.intent.mode === "new_menu" ? (
                  <Input label={t.menuName} value={menuNameInput} maxLength={120} onChange={(e) => setMenuNameInput(e.target.value)} onBlur={() => menuNameInput.trim() && menuNameInput !== draft.intent.menuName && send({ intent: { mode: "new_menu", menuName: menuNameInput.trim() } })} />
                ) : (
                  <Select label={t.targetMenu} placeholder={t.selectMenu} value={String(draft.intent.menuId || "")} fullWidth={false} options={targetMenus.map((m) => ({ value: String(m._id), label: menuOptionLabel(m, lang) }))} onChange={(e) => send({ intent: { mode: draft.intent.mode, menuId: e.target.value } })} />
                )}
              </section>
            )}

            {draft.warnings?.length > 0 && (
              <details className="ai-import-warnings">
                <summary>
                  {t.warningsTitle} ({draft.warnings.length})
                </summary>
                <ul>
                  {draft.warnings.map((w, i) => (
                    <li key={`${w.code}-${i}`}>{w.message}</li>
                  ))}
                </ul>
              </details>
            )}

            <div className="ai-import-toolbar">
              <div className="ai-import-filters" role="group" aria-label="filters">
                {FILTERS.map((key) => (
                  <Button key={key} type="button" variant="primary" appearance={filter === key ? "filled" : "soft"} size="xs" rounded aria-pressed={filter === key} onClick={() => setFilter(key)}>
                    {t.filters[key]} ({counts[key] ?? 0})
                  </Button>
                ))}
              </div>
              {editable && (
                <div className="ai-import-bulk">
                  <Button type="button" variant="success" appearance="soft" size="xs" rounded icon={<CheckCheck size={14} />} onClick={() => sendOps([{ op: "acceptSuggestions", scope: "translations" }])}>
                    {t.acceptTranslations}
                  </Button>
                  <Button type="button" variant="success" appearance="soft" size="xs" rounded icon={<CheckCheck size={14} />} onClick={() => sendOps([{ op: "acceptSuggestions", scope: "descriptions" }])}>
                    {t.acceptDescriptions}
                  </Button>
                  <span className="menu-secondary">{t.bulkNote}</span>
                </div>
              )}
            </div>

            <div className="ai-import-pane-switch" role="tablist">
              <Button type="button" role="tab" aria-selected={pane === "source"} variant="primary" appearance={pane === "source" ? "filled" : "soft"} size="xs" rounded onClick={() => setPane("source")}>
                {t.showSource}
              </Button>
              <Button type="button" role="tab" aria-selected={pane === "draft"} variant="primary" appearance={pane === "draft" ? "filled" : "soft"} size="xs" rounded onClick={() => setPane("draft")}>
                {t.showDraft}
              </Button>
            </div>

            <div className={`ai-import-panes show-${pane}`}>
              <section className="ai-import-pane ai-import-pane-source" aria-label={t.source}>
                <h3 className="ai-import-pane-title">{t.source}</h3>
                <AiImportSourceViewer draft={draft} />
              </section>
              <section className="ai-import-pane ai-import-pane-draft" aria-label={t.draft}>
                <h3 className="ai-import-pane-title">{t.draft}</h3>
                {renderSections()}
              </section>
            </div>
          </>
        )}

        {draft && canWrite && ["needs_review", "failed"].includes(draft.status) && (
          <footer className="ai-import-review-foot">
            <Button type="button" variant="warning" appearance="soft" rounded icon={<Archive size={16} />} onClick={() => setConfirmArchive(true)}>
              {t.archive}
            </Button>
            {draft.status === "needs_review" && (
              <Button type="button" variant="success" appearance="soft" rounded disabled={saving} onClick={() => queueRef.current.then(() => setPlanOpen(true))}>
                {t.reviewPlan}
                {draft.summary?.blocking?.length ? ` (${draft.summary.blocking.length})` : ""}
              </Button>
            )}
            {draft.status === "needs_review" && (
              <Button type="button" variant="success" appearance="filled" rounded icon={<CheckCheck size={16} />} disabled={saving || approveAllOpen || !hasRemaining} onClick={() => queueRef.current.then(() => setApproveAllOpen(true))}>
                {t.approveAll.button}
              </Button>
            )}
          </footer>
        )}
        {draft?.summary?.blocking?.length > 0 && editable && (
          <NoticeBox variant="warning" size="sm" title={t.blockingTitle}>
            <ul className="ai-import-plan-list">
              {draft.summary.blocking.slice(0, 8).map((p, i) => (
                <li key={`${p.code}-${i}`}>{p.message}</li>
              ))}
            </ul>
          </NoticeBox>
        )}
      </div>

      {planOpen && (
        <AiImportPlanDialog
          authorize={allowedNow}
          draftId={draftId}
          onClose={() => setPlanOpen(false)}
          onBlocked={() => load()}
          onApproved={(res) => {
            setPlanOpen(false);
            apply(res.import);
            onApproved?.(res);
          }}
        />
      )}
      {approveAllOpen && (
        <AiImportApproveAllDialog
          authorize={allowedNow}
          draftId={draftId}
          onClose={() => setApproveAllOpen(false)}
          onBlocked={() => load()}
          onApproved={(res) => {
            setApproveAllOpen(false);
            // The review shows the approved draft and the actual result.
            apply(res.import);
            setApproveAllResult(res);
            onChanged?.();
          }}
        />
      )}
      {confirmArchive && (
        <ConfirmModal
          title={t.archive}
          message={t.archiveConfirm}
          confirmText={t.archive}
          cancelText={t.cancel}
          onCancel={() => setConfirmArchive(false)}
          onConfirm={async () => {
            setConfirmArchive(false);
            await archive();
          }}
        />
      )}
    </div>
  );
}
